'use client'

import * as React from 'react'
import { ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from './button'
import {
    DropdownMenu,
    DropdownMenuCheckboxItem,
    DropdownMenuContent,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from './dropdown-menu'

/**
 * FacetedFilter — the platform-standard multi-select filter dropdown.
 * Built on the Radix dropdown primitives (keyboard nav, typeahead, focus
 * restore, ARIA for free). Selection commits immediately on toggle; there
 * is no staged Apply step. Options carry result counts so users never
 * click into an empty list.
 */

export interface FacetedFilterOption {
    value: string
    label: string
    count?: number
}

export interface FacetedFilterProps {
    /** Facet name shown on the trigger and as the menu heading, e.g. "Tags". */
    title: string
    options: FacetedFilterOption[]
    selected: string[]
    onChange: (values: string[]) => void
    /** Custom option rendering (e.g. tinted tag chips); label text still drives typeahead. */
    renderOption?: (option: FacetedFilterOption) => React.ReactNode
    className?: string
}

const MAX_TRIGGER_BADGES = 2

export function FacetedFilter({
    title,
    options,
    selected,
    onChange,
    renderOption,
    className,
}: FacetedFilterProps) {
    if (options.length === 0) return null

    const selectedOptions = options.filter((o) => selected.includes(o.value))

    const toggle = (value: string, checked: boolean) => {
        onChange(checked ? [...selected, value] : selected.filter((v) => v !== value))
    }

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <button
                    type="button"
                    className={cn(
                        'inline-flex h-8 items-center gap-2 px-3 rounded-full border text-sm font-medium leading-5 transition-colors duration-200',
                        // 32px visual pill; pseudo-element pads the hit area
                        // back to 44px (ToolbarTab idiom) — don't remove it
                        "relative before:absolute before:-inset-y-1.5 before:inset-x-0 before:content-['']",
                        'focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--border-focus)] focus-visible:ring-offset-2',
                        selected.length > 0
                            ? // sage, matching FilterChip's selected state (filter role)
                              'bg-[var(--sage-50)] border-[var(--sage-300)] text-[var(--text-sage)] hover:bg-[var(--sage-100)]'
                            : 'bg-[var(--bg-surface)] border-[var(--border-default)] text-[var(--text-primary)] hover:bg-[var(--bg-subtle)]',
                        className
                    )}
                >
                    {title}
                    {selectedOptions.length > 0 && (
                        <span className="flex items-center gap-1">
                            {selectedOptions.length <= MAX_TRIGGER_BADGES ? (
                                selectedOptions.map((o) => (
                                    <span
                                        key={o.value}
                                        className="inline-flex items-center max-w-28 truncate rounded-full px-1.5 h-4.5 text-xs font-medium leading-4 bg-[var(--sage-600)] text-[var(--text-on-brand)]"
                                    >
                                        {o.label}
                                    </span>
                                ))
                            ) : (
                                <span className="inline-flex items-center rounded-full px-1.5 h-4.5 text-xs font-medium leading-4 bg-[var(--sage-600)] text-[var(--text-on-brand)]">
                                    {selectedOptions.length} selected
                                </span>
                            )}
                        </span>
                    )}
                    <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
                </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-60">
                <DropdownMenuLabel>Filter by {title.toLowerCase()}</DropdownMenuLabel>
                <div className="max-h-64 overflow-y-auto">
                    {options.map((option) => (
                        <DropdownMenuCheckboxItem
                            key={option.value}
                            checked={selected.includes(option.value)}
                            onCheckedChange={(checked) => toggle(option.value, checked === true)}
                            // Keep the menu open while picking multiple options
                            onSelect={(e) => e.preventDefault()}
                            className="min-h-11 gap-2"
                        >
                            <span className="flex-1 min-w-0 truncate">
                                {renderOption ? renderOption(option) : option.label}
                            </span>
                            {option.count !== undefined && (
                                <span className="ml-auto shrink-0 text-xs tabular-nums text-[var(--text-tertiary)]">
                                    {option.count}
                                </span>
                            )}
                        </DropdownMenuCheckboxItem>
                    ))}
                </div>
                {selected.length > 0 && (
                    <>
                        <DropdownMenuSeparator />
                        <div className="px-2 py-1.5">
                            <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="w-full min-h-11"
                                onClick={() => onChange([])}
                            >
                                Clear {title.toLowerCase()}
                            </Button>
                        </div>
                    </>
                )}
            </DropdownMenuContent>
        </DropdownMenu>
    )
}
